import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Receipt, Wallet, Banknote, ArrowDownToLine } from 'lucide-react';
import { formatUsd } from '@/lib/receiptsData';

const Stat = ({ label, value, caption, Icon, tone = 'default', testId }) => {
  const accent = {
    default: 'text-[hsl(var(--primary))]',
    success: 'text-[hsl(var(--success))]',
    warning: 'text-[hsl(var(--warning))]',
    danger: 'text-[hsl(var(--destructive))]',
  }[tone];
  const bar = {
    default: 'bg-[hsl(var(--primary))]/60',
    success: 'bg-[hsl(var(--success))]/60',
    warning: 'bg-[hsl(var(--warning))]/60',
    danger: 'bg-[hsl(var(--destructive))]/60',
  }[tone];
  return (
    <Card
      className="relative overflow-hidden border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid={testId}
    >
      <span aria-hidden className={cn('metallic absolute left-0 top-0 h-full w-[2px]', bar)} />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] leading-snug sm:text-sm lg:text-xs 2xl:text-sm font-bold uppercase tracking-[0.12em] break-words text-[hsl(var(--primary))]">
            {label}
          </span>
          {Icon ? <Icon size={16} className={cn('opacity-80', accent)} /> : null}
        </div>
        <div
          className="mt-1.5 text-lg sm:text-xl font-semibold tracking-tight tabular-nums text-foreground"
          data-testid={`${testId}-value`}
        >
          {value}
        </div>
        {caption ? (
          <div className="mt-1 text-xs text-muted-foreground">{caption}</div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export const ReceiptStatsBar = ({ stats }) => {
  const { t } = useTranslation();
  const { count = 0, total = 0, rebate = 0, netCost = 0, byStore = {} } = stats || {};
  const topStore =
    Object.entries(byStore)
      .sort((a, b) => (b[1] || 0) - (a[1] || 0))[0] || null;
  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5"
      data-testid="receipts-stats"
    >
      <Stat
        label={t('receiptScanner.stats.logged')}
        value={count}
        caption={
          topStore
            ? t('receiptScanner.stats.loggedCaptionTop', {
                store: topStore[0],
                amount: formatUsd(topStore[1]),
              })
            : t('receiptScanner.stats.loggedCaptionNone')
        }
        Icon={Receipt}
        testId="receipt-stat-count"
      />
      <Stat
        label={t('receiptScanner.stats.totalSpent')}
        value={formatUsd(total)}
        caption={t('receiptScanner.stats.totalCaption')}
        Icon={Wallet}
        tone="warning"
        testId="receipt-stat-total"
      />
      <Stat
        label={t('receiptScanner.stats.expectedRebate')}
        value={formatUsd(rebate)}
        caption={t('receiptScanner.stats.rebateCaption')}
        Icon={ArrowDownToLine}
        tone="success"
        testId="receipt-stat-rebate"
      />
      <Stat
        label={t('receiptScanner.stats.netCost')}
        value={formatUsd(netCost)}
        caption={t('receiptScanner.stats.netCaption')}
        Icon={Banknote}
        testId="receipt-stat-net"
      />
    </div>
  );
};

export default ReceiptStatsBar;
